import _ from "lodash";
import React, { useState } from "react";
import { Sentry } from "services/sentry";
import classes from "./AddYoutubeReference.scss";
import { YouTubeBrand } from "@toddle-edu/ds-icons";
import {
  Button,
  IconButton,
  Modal,
  TextInput,
  Tooltip,
} from "@toddle-edu/ds-web";
import { SHIFT_ENTER_SUBMIT_SHORTCUT } from "Constants/stringConstants";
import FullScreenLoader from "UIComponents/FullScreenLoader";

const YOUTUBE_URL_VALIDATION_RE = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|\?v=)([^#\&\?]*).*/;

/**
 *
 * @param {*} url
 * @returns Return Youtube ID
 */
const getYoutubeID = url => {
  const match = url.match(YOUTUBE_URL_VALIDATION_RE);
  if (match && _.isEqual(_.get(match, "[2].length"), 11)) {
    return match[2];
  } else {
    return null;
  }
};

/**
 *
 * @param {*} youtubeID
 * @returns Return Youtube Title
 */
const getYoutubeTitle = async youtubeID => {
  const youtubeEmbedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${youtubeID}&format=json`;

  try {
    const res = await fetch(youtubeEmbedUrl);
    if (res.ok) {
      const out = await res.json();
      if (out) {
        return _.get(out, "title", "Youtube video");
      }
      return "Youtube video";
    }
    return "Youtube video";
  } catch (error) {
    Sentry.captureMessage(`GetYoutubeInfoError: ${error?.message}`, {
      extra: { youtubeID },
    });
    return "Youtube video";
  }
};

/**
 *
 * @param {*} props
 * @returns Add Youtube link
 */
const AddYoutubeReference = props => {
  const { t, onUpload, setToastMsg } = props;
  const modalTitle = "Add youtube link";
  const testId = "worksheet-ai-add-media-reference-youtube-link";
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [link, setLink] = useState("");

  const onAddYoutubeLink = async () => {
    setIsLoading(true);
    const youtubeLink = _.trim(link);
    const youtubeID = getYoutubeID(youtubeLink);
    if (youtubeID) {
      const title = await getYoutubeTitle(youtubeID);
      const uploadObject = {
        url: youtubeLink,
        name: title || "Youtube",
        type: "LINK",
        mimeType: "VIDEO",
        description: "Youtube video",
      };
      onUpload(uploadObject, () => {
        setLink("");
        setShowModal(false);
        setIsLoading(false);
      });
    } else {
      setToastMsg(t("worksheet:youtube_link_invalid"));
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        dsVersion="2.0"
        tooltip={t("worksheet:youtube_link")}
        showArrow={true}
        placement="bottom"
      >
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="outlined"
          size="small"
          icon={<YouTubeBrand />}
          onClick={() => setShowModal(true)}
          testId="worksheet-media-reference-add-youtube"
        />
      </Tooltip>
      {showModal && (
        <Modal
          width="480px"
          height="fit-content"
          dsVersion="2.0"
          isOpen={true}
          onClose={() => setShowModal(false)}
        >
          <Modal.Header title={modalTitle} />
          <div className={classes.youtubeModalBody}>
            <div className={classes.youtubeModalBodyLink}>
              <TextInput
                name="link"
                value={link}
                label={t("worksheet:youtube_link")}
                dsVersion="2.0"
                disabled={isLoading}
                testId={`${testId}-linkModal-link`}
                placeholder={t("worksheet:add_url_here")}
                onChange={e => setLink(e.target.value)}
              />
            </div>
          </div>
          <Modal.Footer>
            <div className={classes.youtubeModalFooterButtons}>
              <Button
                dsVersion="2.0"
                size="medium"
                onClick={() => setShowModal(false)}
                variant="neutral"
                type={"outlined"}
                disabled={isLoading}
                shouldStopPropagation={true}
                testId={`${testId}-linkModal-cancel`}
              >
                {t("common:discard")}
              </Button>
              <Button
                dsVersion="2.0"
                size="medium"
                variant="primary"
                testId={`${testId}-linkModal-submit`}
                onClick={onAddYoutubeLink}
                disabled={isLoading}
                type="fill"
                shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
                shouldStopPropagation={true}
              >
                {t("common:save")}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
      {isLoading && <FullScreenLoader />}
    </>
  );
};

export default AddYoutubeReference;
